import Cookies from 'universal-cookie';
import { betterFetch } from 'utils/api/helper';

const {
  REACT_APP_NAUTH_CLIENT_ID: clientId,
  REACT_APP_NAUTH_URL_V1: url,
  REACT_APP_NAUTH_TOKEN_COOKIE_V1: nauthTokenCookie,
  REACT_APP_NAUTH_ID_TOKEN_COOKIE: nauthIdTokenCookie,
  REACT_APP_NAUTH_CODE_VERIFIER: codeVerifier
} = process.env;
const cookies = new Cookies();

export const NAUTH_URL = url;

/**
 * call nauth login endpoint
 * @param {object} params - object of function params
 * @param {string} params.redirectUri - an absolute uri to redirect to after nauth login
 * @param {string} params.state - an encoded action to open the app after authentication
 * @param {boolean} [params.code] - whether nauth should check for signle sign on
 * @returns {string} a nauth login url
 */
const login = ({ redirectUri, state, code }) => {
  const body = JSON.stringify({
    client_id: clientId,
    redirect_uri: redirectUri,
    grant_type: 'authorization_code',
    code,
    code_verifier: codeVerifier,
    state
  });

  const headers = {
    accept: 'application/json',
    'content-type': 'application/json'
  };
  return betterFetch(`${NAUTH_URL}/token`, {
    method: 'post',
    body,
    headers
  });
};

/**
 * call nauth revoke/logout endpoint
 * @returns {Promise<Response>} the reponse object
 */
const logout = () => {
  const headers = {
    accept: 'application/json',
    'content-type': 'application/json'
  };
  const options = {
    method: 'POST',
    headers,
    body: JSON.stringify({
      client_id: clientId,
      token: cookies.get(nauthTokenCookie),
      token_type_hint: 'access_token'
    })
  };

  return betterFetch(`${NAUTH_URL}/revoke`, options);
};

/**
 * get nauth token cookie
 * @returns {string} nauth token cookie value
 */
const getNauthTokenCookie = () => {
  return cookies.get(nauthTokenCookie);
};

/**
 * set nauth cookie
 * @param {string} value - value for the cookie
 * @param {number} maxAge - max age for the cookie
 */
const setNauthTokenCookie = (value, maxAge) => {
  cookies.set(nauthTokenCookie, value, {
    maxAge,
    path: '/'
  });

  // set the timestamp of nauth token expiration so it can be used in the app
  const now = Math.floor(Date.now() / 1000);
  cookies.set(`${nauthTokenCookie}Expiration`, maxAge + now, {
    maxAge,
    path: '/'
  });
};

/**
 * get nauth id token cookie
 * @returns {string} nauth id token cookie value
 */
const getNauthIdTokenCookie = () => {
  return cookies.get(nauthIdTokenCookie);
};

/**
 * set nauth id token cookie
 * @param {string} value - value for the cookie
 * @param {number} maxAge - max age for the cookie
 */
const setNauthIdTokenCookie = (value, maxAge) => {
  cookies.set(nauthIdTokenCookie, value, {
    maxAge,
    path: '/'
  });
};

/**
 * remove nauth token cookie
 */
const removeNauthTokenCookie = () => {
  cookies.remove(nauthTokenCookie, { path: '/' });
};

/**
 * remove nauth id token cookie
 */
const removeNauthIdTokenCookie = () => {
  cookies.remove(nauthIdTokenCookie, { path: '/' });
};

export default {
  login,
  logout,
  getNauthTokenCookie,
  setNauthTokenCookie,
  removeNauthTokenCookie,
  getNauthIdTokenCookie,
  setNauthIdTokenCookie,
  removeNauthIdTokenCookie
};
